import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { SECRET_MASK, STUDIO_CONNECTIONS_COLLECTION } from "../constants";

function nowIso() {
  return new Date().toISOString();
}

const SECRET_FIELDS = ["apiKey"];
const SERVICE_ACCOUNT_ENC_VERSION = "sa_enc_v1";

export function maskSecret(value) {
  if (!value) return "";
  return SECRET_MASK;
}

export function isMaskedSecret(value) {
  return value === SECRET_MASK;
}

export function emptyConnectionForm() {
  return {
    projectId: "",
    name: "",
    environment: "development",
    firebaseProjectId: "",
    apiKey: "",
    authDomain: "",
    storageBucket: "",
    messagingSenderId: "",
    appId: "",
    measurementId: "",
    region: "",
    serviceAccountEmail: "",
    serviceAccountEnc: "",
    serviceAccountEncVersion: SERVICE_ACCOUNT_ENC_VERSION,
    favorite: false,
    active: false,
  };
}

export function normalizeConnection(docSnap, { includeSecrets = false } = {}) {
  const data = docSnap.data() || {};
  const conn = {
    id: docSnap.id,
    projectId: data.projectId || "",
    name: data.name || "",
    environment: data.environment || "development",
    firebaseProjectId: data.firebaseProjectId || "",
    apiKey: includeSecrets ? data.apiKey || "" : maskSecret(data.apiKey),
    authDomain: data.authDomain || "",
    storageBucket: data.storageBucket || "",
    messagingSenderId: data.messagingSenderId || "",
    appId: data.appId || "",
    measurementId: data.measurementId || "",
    region: data.region || "",
    status: data.status || "unknown",
    favorite: Boolean(data.favorite),
    active: Boolean(data.active),
    archived: Boolean(data.archived),
    createdAt: data.createdAt || "",
    updatedAt: data.updatedAt || "",
    lastVerified: data.lastVerified || "",
    createdBy: data.createdBy || "",
    hasApiKey: Boolean(data.apiKey),
    serviceAccountEmail: data.serviceAccountEmail || "",
    serviceAccountEnc: data.serviceAccountEnc || "",
    serviceAccountEncVersion: data.serviceAccountEncVersion || SERVICE_ACCOUNT_ENC_VERSION,
    hasServiceAccount:
      Boolean(data.serviceAccountEnc) ||
      Boolean(data.serviceAccountJson) ||
      Boolean(data.serviceAccountPrivateKey) ||
      Boolean(data.serviceAccountEmail),
  };
  return conn;
}

export async function listStudioConnections({ projectId } = {}) {
  let snap;
  if (projectId) {
    snap = await getDocs(
      query(collection(db, STUDIO_CONNECTIONS_COLLECTION), where("projectId", "==", projectId))
    );
  } else {
    snap = await getDocs(collection(db, STUDIO_CONNECTIONS_COLLECTION));
  }
  return snap.docs
    .map((d) => normalizeConnection(d))
    .sort((a, b) => {
      const ta = a.updatedAt || a.createdAt || "";
      const tb = b.updatedAt || b.createdAt || "";
      return tb.localeCompare(ta);
    });
}

export async function getStudioConnection(id, { includeSecrets = false } = {}) {
  const snap = await getDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id));
  if (!snap.exists()) return null;
  return normalizeConnection(snap, { includeSecrets });
}

async function deactivateSiblings(projectId, excludeId, actor) {
  const siblings = await listStudioConnections({ projectId });
  const ts = nowIso();
  await Promise.all(
    siblings
      .filter((c) => c.id !== excludeId && c.active)
      .map((c) =>
        updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, c.id), {
          active: false,
          updatedAt: ts,
          updatedBy: actor || "",
        })
      )
  );
}

function buildConnectionPayload(payload, existing, createdBy) {
  const ts = nowIso();
  const data = {
    projectId: String(payload.projectId || "").trim(),
    name: String(payload.name || "").trim(),
    environment: payload.environment || "development",
    firebaseProjectId: String(payload.firebaseProjectId || "").trim(),
    authDomain: String(payload.authDomain || "").trim(),
    storageBucket: String(payload.storageBucket || "").trim(),
    messagingSenderId: String(payload.messagingSenderId || "").trim(),
    appId: String(payload.appId || "").trim(),
    measurementId: String(payload.measurementId || "").trim(),
    region: String(payload.region || "").trim(),
    favorite: Boolean(payload.favorite),
    active: Boolean(payload.active),
    archived: Boolean(payload.archived),
    updatedAt: ts,
  };

  const apiKey = String(payload.apiKey || "").trim();
  if (apiKey && !isMaskedSecret(apiKey)) {
    data.apiKey = apiKey;
  } else if (existing?.apiKey && !isMaskedSecret(existing.apiKey)) {
    data.apiKey = existing.apiKey;
  }

  if (!existing) {
    data.status = "unknown";
    data.createdAt = ts;
    data.createdBy = createdBy || "";
    data.lastVerified = "";
    if (!data.apiKey) data.apiKey = "";
  }

  // Optional service-account credentials for cross-project Admin SDK discovery.
  // Stored encrypted-at-rest (ciphertext + metadata), never plaintext.
  const encIncoming = String(payload.serviceAccountEnc || "").trim();
  const emailIncoming = String(payload.serviceAccountEmail || "").trim();
  const encExisting = existing?.serviceAccountEnc || "";
  const emailExisting = existing?.serviceAccountEmail || "";

  const encToStore = encIncoming ? encIncoming : encExisting;
  const emailToStore = encIncoming ? emailIncoming : emailExisting;

  if (encToStore) {
    data.serviceAccountEnc = encToStore;
    data.serviceAccountEmail = emailToStore;
    data.serviceAccountEncVersion = payload.serviceAccountEncVersion || SERVICE_ACCOUNT_ENC_VERSION;
  }

  return data;
}

export async function createStudioConnection(payload, createdBy) {
  const name = String(payload.name || "").trim();
  const projectId = String(payload.projectId || "").trim();
  const firebaseProjectId = String(payload.firebaseProjectId || "").trim();
  if (!name) throw new Error("Connection name is required");
  if (!projectId) throw new Error("Project is required");
  if (!firebaseProjectId) throw new Error("Firebase Project ID is required");

  const data = buildConnectionPayload(payload, null, createdBy);
  if (data.active) await deactivateSiblings(projectId, null, createdBy);

  const ref = await addDoc(collection(db, STUDIO_CONNECTIONS_COLLECTION), data);
  return getStudioConnection(ref.id);
}

export async function updateStudioConnection(id, payload, existingRaw, updatedBy) {
  const name = String(payload.name || "").trim();
  if (!name) throw new Error("Connection name is required");
  if (!String(payload.firebaseProjectId || "").trim()) {
    throw new Error("Firebase Project ID is required");
  }

  const existing = existingRaw || (await getStudioConnection(id, { includeSecrets: true }));
  const data = buildConnectionPayload({ ...existing, ...payload }, existing, updatedBy);

  if (data.active) await deactivateSiblings(data.projectId, id, updatedBy);

  await updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id), {
    ...data,
    updatedBy: updatedBy || "",
  });
  return getStudioConnection(id);
}

export async function setConnectionArchived(id, archived, actor) {
  const patch = {
    archived: Boolean(archived),
    updatedAt: nowIso(),
    updatedBy: actor || "",
  };
  if (archived) patch.active = false;
  await updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id), patch);
  return getStudioConnection(id);
}

export async function setConnectionFavorite(id, favorite, actor) {
  await updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id), {
    favorite: Boolean(favorite),
    updatedAt: nowIso(),
    updatedBy: actor || "",
  });
  return getStudioConnection(id);
}

export async function setConnectionActive(id, active, actor) {
  const conn = await getStudioConnection(id, { includeSecrets: true });
  if (!conn) throw new Error("Connection not found");
  if (active) await deactivateSiblings(conn.projectId, id, actor);
  await updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id), {
    active: Boolean(active),
    updatedAt: nowIso(),
    updatedBy: actor || "",
  });
  return getStudioConnection(id);
}

export async function setConnectionStatus(id, status, { lastVerified } = {}) {
  const patch = { status, updatedAt: nowIso() };
  if (lastVerified) patch.lastVerified = lastVerified;
  await updateDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id), patch);
  return getStudioConnection(id);
}

export async function deleteStudioConnection(id) {
  await deleteDoc(doc(db, STUDIO_CONNECTIONS_COLLECTION, id));
}

export async function duplicateStudioConnection(sourceId, createdBy) {
  const source = await getStudioConnection(sourceId, { includeSecrets: true });
  if (!source) throw new Error("Source connection not found");

  return createStudioConnection(
    {
      ...source,
      name: `${source.name} (Copy)`,
      favorite: false,
      active: false,
      archived: false,
      apiKey: source.apiKey || "",
    },
    createdBy
  );
}

export function computeConnectionStats(connections) {
  const active = connections.filter((c) => !c.archived);
  return {
    total: connections.length,
    production: active.filter((c) => c.environment === "production").length,
    testing: active.filter((c) => c.environment === "testing").length,
    demo: active.filter((c) => c.environment === "demo").length,
    offline: active.filter((c) => c.status === "offline").length,
    online: active.filter((c) => c.status === "online").length,
    failed: active.filter((c) => c.status === "failed").length,
    warning: active.filter((c) => c.status === "warning").length,
  };
}

/** Internal use for verification — loads raw secrets. */
export async function getConnectionSecrets(id) {
  return getStudioConnection(id, { includeSecrets: true });
}
